<template>
  <VDataGridV2
    :skeleton-row-count="16"
    id="assembly-registry-expanded"
    :loader="dataLoader"
    paginated
    group-children-property="positions"
    entity-type="assembly"
    :group-context-menu="AssembliesService.contextMenu"
    :selectable="selectableMode"
    :selected-rows="selectedPositions"
    :disable-row-when="isRowDisabled"
    @group:dblclick="openAssemblyCard"
    @row:selected="onRowSelected"
    @row:deselected="onRowDeselected"
  >
    <VDataGridGroupV2>
      <template #default="{ parent: row }">

        <CommentsButton
          entity-type-name="assembly"
          :entity="row"
          v-if="$core.currentPartner.isBuyer === true"
        />

        <div>
          <VLink @click.stop="openAssemblyCard(row)">{{ row.formattedNumber }}</VLink>
          от {{ $core.utils.date.format(row.createdAt) }}
        </div>

        <span>{{ row.seller.name }}</span>

        Готовность {{ $core.utils.date.humanizeDate(row.assemblyDate, null, true) }}

        <VLink
          @click.stop="AssembliesService.showAssemblyInfoPopup(row, $event.currentTarget)"
          material="info"
        />

        <VBadges>
          <VBadge v-if="!row.isOfferBased" class="primary">Прямой</VBadge>
          <VBadge v-if="row.isSamples">Образцы</VBadge>
        </VBadges>
      </template>
    </VDataGridGroupV2>

    <VDataGridColumnV2
      content-class="comment"
    >
      <template #default="{ row }">
        <CommentsButton
          entity-type-name="orderPosition"
          :entity="row.orderPosition"
          v-if="$core.currentPartner.isBuyer === true"
        />
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="id"
      sort-default-value="desc"
      filterable
      label="№"
      sortable
      content-align="left"
      filter-send-name="idPattern"
      :filter-props="{ placeholder: '№', searchable: true, clearable: true }"
      width="100px"
    >
      <template #default="{ row }">
        <VLink @click="OrdersService.showOrderPositionCard(row.orderPosition)">
          {{ $core.utils.string.formatWithDelimiter(row.orderPosition.id) }}
        </VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="buyersCode"
      label="Арт"
      filterable
      content-align="center"
      filter-identity="*"
      filter-send-name="codePattern"
      :filter-props="{ placeholder: 'Артикул', searchable: true, clearable: true }"
      content-class="ellipsis"
      width="100px"
    >
      <template #default="{ row }">
        <VLink style="width: 100%" class="ellipsis" @click="OrdersService.showCodeInfoPopup(row.orderPosition, $event.currentTarget)">
          {{ row.orderPosition.buyersCode ?? '--' }}
        </VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="product"
      class="name"
      filterable
      filter-type="select"
      filter-identity="*"
      :filter-props="{ placeholder: 'Название или артикул товара', searchable: true, dataUrl: 'products.getList' }"
      label="Товар"
      content-class="ellipsis"
      width="350px"
    >
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.name"
        >
          {{ row.orderPosition.name }}
        </div>
        <VBadge v-if="row.orderPosition.isForTestsOnly">Не под план</VBadge>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="qty"
      label="Кол-во"
      content-align="right"
      content-class="nowrap"
    >
      <template #default="{ row }">
        {{ row.qty.formatSmart(row.orderPosition.unit) }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="sum"
      content-align="right"
      content-class="nowrap"
      width="150px"
      label="Цена товара"
    >
      <template #default="{ row }">
        {{ row.orderPosition.priceFullSeller.format(row.orderPosition.currency, row.orderPosition.unit) }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="qtyShipped"
      content-align="right"
      width="130px"
      label="Отгружено"
    >
      <template #default="{ row }">
        <template v-if="row">
          <template v-if="row.isShipped">
            {{ row.qtyShipped.formatSmart(row.orderPosition.unit) }}

            <div class="description nowrap">
              {{ row.isShippedEntirely ? 'Полностью' : 'Частично' }}
            </div>
          </template>

          <template v-else>
            Нет
          </template>
        </template>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="desiredReceiptPeriod"
      filterable
      filter-type="date-range"
      :filter-props="{ placeholder: 'Выберите период', clearable: true }"
      label="Дата доставки"
      header-class="nowrap"
      width="150px"
    >
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="$core.utils.date.humanizeDate(row.orderPosition.desiredReceiptDate, null)"
        >
          {{ $core.utils.date.humanizeDate(row.orderPosition.desiredReceiptDate, null) }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="plannedShipmentPeriod"
      filterable
      filter-type="date-range"
      :filter-props="{ placeholder: 'Выберите период', clearable: true }"
      label="Дата отгрузки"
      header-class="nowrap"
      width="150px"
    >
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="$core.utils.date.humanizeDate(row.orderPosition.plannedShipmentDate, null)"
        >
          {{ $core.utils.date.humanizeDate(row.orderPosition.plannedShipmentDate, null) }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2 v-if="showActionBar === true || hasShipment === true">
      <template #default>
        <template v-if="hasShipment === true">
          <div class="nowrap">
            Выбрано {{ shipment.positions.length }} позици{{ $core.utils.regional.ru.pluralize(shipment.positions.length, 'я', 'и', 'й') }}
          </div>

          <VButton
            theme="primary"
            :disabled="shipment.positions.length === 0"
            :title="shipment.positions.length === 0 ? 'Выберите позиции' : null"
            @click="$emit('done')"
          >
            Перейти к отгрузке
          </VButton>
        </template>

        <VButton v-else @click="openShipmentDialog()">
          Создать отгрузку
        </VButton>
      </template>
    </VDataGridActionBarV2>
  </VDataGridV2>
</template>

<script lang="ts">

import type Shipment             from "@/domains/Shipments/Shipment";
import DataGridLoader            from '@/lib/components/VDataGridV2/DataGridLoader';
import Assembly                  from '@/domains/Assemblies/Assembly';
import AssemblyPosition          from '@/domains/Assemblies/AssemblyPosition';
import AssembliesService         from '@/domains/Assemblies/AssembliesService';
import TransportOrdersService    from "@/domains/TransportOrders/TransportOrdersService";
import OrdersService             from "@/domains/Orders/OrdersService";
import ShipmentsService          from "@/domains/Shipments/ShipmentsService";
import ShipmentPosition          from "@/domains/Shipments/ShipmentPosition";

export default defineComponent({
  name: 'AssembliesRegistryExpanded',

  props: {
    /**
     * Наполняемая отгрузка. Если передана — выбор позиций сборок в реестре
     * обогащает shipment: строка (AssemblyPosition) конвертируется в
     * ShipmentPosition через ShipmentPosition.fromAssemblyPosition и пишется
     * в shipment.positions. Без неё реестр работает в обычном режиме просмотра.
     */
    shipment: {
      type:    Object as PropType<Shipment>,
      default: null,
    },

    showActionBar: {
      type:    Boolean,
      default: false,
    },

    params: {
      type: Object as PropType<Parameters<typeof AssembliesService.getListPaginated>[0]>,
      default: null
    }
  },

  emits: ['done'],


  setup() {
    return {
      AssembliesService,
      TransportOrdersService,
      OrdersService,
    };
  },

  data() {
    const extraParams: Record<string, unknown> = {
      requirePositions:    true,
      requireCommentsInfo: true,
    };

    /** Отгрузка — проп, за время жизни реестра не меняется: её фильтры статичные */
    if (this.shipment !== null) {
      extraParams.seller            = this.shipment.seller;
      extraParams.isShippedEntirely = false;
    }

    return {
      dataLoader: new DataGridLoader(AssembliesService.getListPaginated)
        .useExtraParams(extraParams)
        /** params — проп, может смениться после создания лоадера, поэтому
         *  читается здесь, а не в статичных useExtraParams */
        .useReactiveSource(() => {
          return { ...this.params };
        }),

      assemblyInfoPopup: {
        target:  null,
        visible: false,
        entity:  null,
      },
    };
  },

  computed: {
    /**
     * Реестр работает в режиме наполнения отгрузки, если shipment передан.
     * В этом режиме selectable всегда включён и выбор пишется в shipment.positions.
     */
    hasShipment(): boolean {
      return this.shipment !== null;
    },

    /**
     * Выбор включён только в режиме наполнения отгрузки.
     */
    selectableMode(): boolean {
      return this.hasShipment;
    },

    /**
     * Выбранные строки — это assemblyPosition'ы, уже добавленные в отгрузку.
     * Источник истины сам shipment.positions; достаём из каждой ShipmentPosition
     * её assemblyPosition, потому что строки грида — это AssemblyPosition.
     */
    selectedPositions(): AssemblyPosition[] {
      if (this.hasShipment === false) return [];
      return this.shipment.positions.map(shipmentPosition => shipmentPosition.assemblyPosition);
    },
  },

  methods: {
    async openAssemblyCard(assembly: Assembly): Promise<void> {
      if (await AssembliesService.showAssemblyCard(assembly) === true) {
        await this.dataLoader.refresh();
      }
    },

    /** Реестр обновляем, только если отгрузку сохранили (resolve карточки) */
    async openShipmentDialog(): Promise<void> {
      if (await ShipmentsService.showShipmentCard() === true) {
        await this.dataLoader.refresh();
      }
    },

    /**
     * Дизейбл строки в режиме наполнения отгрузки.
     * Возвращает строку-причину (станет тултипом disabled-строки) либо false,
     * если позицию можно добавить. Без shipment всегда false.
     *
     * Nota bene: в этом режиме реестр открыт в диалоге поверх карточки отгрузки, и backdrop-filter
     * оверлея VDialogV3 (blur) сильно бьёт по производительности: при наведении на строки
     * заметны фризы. Без backdrop-filter реестр рисуется заметно быстрее.
     */
    isRowDisabled(assemblyPosition: AssemblyPosition): string | false {
      if (this.hasShipment === false) return false;

      const assembly = this.dataLoader.data.find((_assembly: Assembly) => _assembly.positions.includes(assemblyPosition));

      const isAdded = this.shipment.positions.some(
        shipmentPosition => shipmentPosition.assemblyPosition?.id === assemblyPosition.id,
      );

      let reason: string | false = false;

      if (isAdded === true) {
        // уже в отгрузке — снять отметку можно всегда
      }
      else if (assemblyPosition.isShippedEntirely === true) {
        reason = 'Позиция уже отгружена';
      }
      else if (assembly?.sellerStore?.id !== this.shipment.sellerStore?.id) {
        reason = 'Склад отправки сборки не совпадает со складом отгрузки';
      }
      else if (assembly?.buyerStore?.id !== this.shipment.buyerStore?.id) {
        reason = 'Склад доставки сборки не совпадает со складом отгрузки';
      }
      else {
        reason = this.shipment.getReasonCannotAddPosition(ShipmentPosition.fromAssemblyPosition(assemblyPosition)) ?? false;
      }

      return reason;
    },

    /**
     * Колбэк выбора строки. Конвертируем AssemblyPosition в ShipmentPosition
     * и добавляем в отгрузку.
     */
    onRowSelected(assemblyPosition: AssemblyPosition): void {
      if (this.hasShipment === false) return;
      const shipmentPosition = ShipmentPosition.fromAssemblyPosition(assemblyPosition);
      this.shipment.addPosition(shipmentPosition);
    },

    /**
     * Колбэк снятия строки. Находим в отгрузке ShipmentPosition по
     * assemblyPosition.id и удаляем по индексу (сигнатура Shipment.deletePosition).
     */
    onRowDeselected(assemblyPosition: AssemblyPosition): void {
      if (this.hasShipment === false) return;
      const index = this.shipment.positions.findIndex(
        shipmentPosition => shipmentPosition.assemblyPosition.id === assemblyPosition.id,
      );
      if (index === -1) return;
      this.shipment.deletePosition(null, index);
    },

    refresh(): void {
      this.dataLoader.refresh();
    },
  },
});
</script>